import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/apiUtils';
import { useAuth } from './authProvider/AuthContext';
import '../css/App.css';

const UserManagementTab = () => {
    const { user: authUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [usageData, setUsageData] = useState(null);
    const [walletSummary, setWalletSummary] = useState(null);
    const [adjustDelta, setAdjustDelta] = useState('');
    const [adjustReason, setAdjustReason] = useState('');

    const fetchUsers = useCallback(async (page = 1, search = '') => {
        try {
            const response = await apiCall('/search-profiles', null, {
                method: 'GET',
                params: {
                    page: page,
                    page_size: 20,
                    query: search
                }
            });
            setUsers(response.items);
            setPagination(prev => ({
                ...prev,
                current: page,
                total: response.pagination.total_count
            }));
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    }, []);

    const fetchUserUsage = async (userEmail) => {
        try {
            const response = await apiCall('/get-usage-stats', null, {
                method: 'GET',
                params: { email: userEmail }
            });
            setUsageData(response);
        } catch (error) {
            console.error('Failed to fetch usage data:', error);
        }
    };

    const updateUserRole = async (userEmail, newRole) => {
        try {
            await apiCall('/update-profile', {
                email: userEmail,
                role: newRole
            }, {
                method: 'PUT'
            });
            fetchUsers(pagination.current, searchText);
        } catch (error) {
            console.error('Failed to update user role:', error);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSearch = (value) => {
        setSearchText(value);
        fetchUsers(1, value);
    };

    const fetchUserWallet = async (userEmail) => {
        try {
            const response = await apiCall('/billing/admin/user-summary', null, {
                method: 'GET',
                params: { email: userEmail }
            });
            setWalletSummary(response);
        } catch (error) {
            console.error('Failed to fetch wallet summary:', error);
            setWalletSummary(null);
        }
    };

    const handleAdjustCredits = async () => {
        if (!selectedUser || !adjustDelta) return;
        try {
            await apiCall('/billing/admin/adjust-credits', {
                targetEmail: selectedUser.email,
                delta: parseInt(adjustDelta, 10),
                reason: adjustReason || 'admin_adjustment'
            }, { method: 'POST' });
            setAdjustDelta('');
            setAdjustReason('');
            await fetchUserWallet(selectedUser.email);
        } catch (error) {
            console.error('Failed to adjust credits:', error);
        }
    };

    const showUserDetails = async (user) => {
        setSelectedUser(user);
        setWalletSummary(null);
        await Promise.all([
            fetchUserUsage(user.email),
            fetchUserWallet(user.email),
        ]);
        setIsModalVisible(true);
    };

    return (
        <div className="user-management-tab">
            <div className="user-management-header">
                <input
                    type="text"
                    placeholder="Search users"
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="blog-search-input"
                />
            </div>

            <div className="blogs-table">
                <table>
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Credits</th>
                            <th>Last Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.email}>
                                <td>
                                    <button
                                        className="edit-button"
                                        onClick={() => showUserDetails(user)}
                                    >
                                        {user.email}
                                    </button>
                                </td>
                                <td>
                                    <select
                                        value={user.role || 'user'}
                                        onChange={(e) => updateUserRole(user.email, e.target.value)}
                                        disabled={!authUser?.isAdmin}
                                        className="sort-select"
                                    >
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </td>
                                <td>{user.billing?.availableCredits ?? '—'}</td>
                                <td>{user.lastActive ? new Date(user.lastActive).toLocaleDateString() : 'Never'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isModalVisible && selectedUser && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>User Details</h3>
                        <div className="user-details">
                            <div className="user-info">
                                <h4>{selectedUser.email}</h4>
                                <p>Role: {selectedUser.role || 'user'}</p>
                                <p>Account status: {walletSummary?.billing?.accountStatus || '—'}</p>
                            </div>

                            {walletSummary && (
                                <div className="usage-stats">
                                    <h4>Wallet</h4>
                                    <div className="stats-grid">
                                        <div className="stat-item">
                                            <span className="stat-label">Available credits</span>
                                            <span className="stat-value">{walletSummary.billing?.availableCredits ?? 0}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Lifetime purchased</span>
                                            <span className="stat-value">{walletSummary.billing?.lifetimeCreditsPurchased ?? 0}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Lifetime used</span>
                                            <span className="stat-value">{walletSummary.billing?.lifetimeCreditsUsed ?? 0}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Trial claimed</span>
                                            <span className="stat-value">{walletSummary.billing?.trialCreditsClaimed ? 'Yes' : 'No'}</span>
                                        </div>
                                    </div>

                                    <h4 style={{ marginTop: '16px' }}>Adjust Credits</h4>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <input
                                            type="number"
                                            placeholder="Delta (e.g. +30 or -10)"
                                            value={adjustDelta}
                                            onChange={(e) => setAdjustDelta(e.target.value)}
                                            className="blog-search-input"
                                            style={{ width: '140px' }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Reason"
                                            value={adjustReason}
                                            onChange={(e) => setAdjustReason(e.target.value)}
                                            className="blog-search-input"
                                            style={{ width: '160px' }}
                                        />
                                        <button className="submit-button" onClick={handleAdjustCredits}>
                                            Apply
                                        </button>
                                    </div>

                                    {walletSummary.recentLedger?.length > 0 && (
                                        <>
                                            <h4 style={{ marginTop: '16px' }}>Recent Ledger</h4>
                                            <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                                                <table style={{ width: '100%', fontSize: '0.82rem' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Type</th>
                                                            <th>Delta</th>
                                                            <th>Balance after</th>
                                                            <th>Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {walletSummary.recentLedger.map((entry) => (
                                                            <tr key={entry.id}>
                                                                <td>{entry.type}</td>
                                                                <td style={{ color: entry.creditsDelta >= 0 ? '#1a7a1a' : '#a01a1a' }}>
                                                                    {entry.creditsDelta >= 0 ? `+${entry.creditsDelta}` : entry.creditsDelta}
                                                                </td>
                                                                <td>{entry.balanceAfter}</td>
                                                                <td>{entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {usageData && (
                                <div className="usage-stats" style={{ marginTop: '16px' }}>
                                    <h4>Legacy Usage</h4>
                                    <div className="stats-grid">
                                        <div className="stat-item">
                                            <span className="stat-label">Blogs Generated</span>
                                            <span className="stat-value">{usageData.blogCount || 0}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Last Updated</span>
                                            <span className="stat-value">
                                                {usageData.lastUpdated ? new Date(usageData.lastUpdated).toLocaleString() : 'Never'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button
                                className="cancel-button"
                                onClick={() => setIsModalVisible(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagementTab; 